import type { Command } from 'commander';
import { OAUTH_SESSIONS_BASE } from '@wport/core';
import { unwrapDataResponse } from '@wport/core';
import { personalDelete, type PersonalRequestOptions } from '../../lib/personal-client';
import { resolveContext, type ResolvedContext } from '../../lib/global-opts';
import { CliError, ExitCode } from '../../lib/errors';
import { printJson } from '../../lib/output';

interface RevokeFlags {
	allOthers?: boolean;
}

/** `DELETE /oauth/sessions/others` 契約：DataResponse<{ revoked_count }>（SoT = personal-sessions.controller.ts）。 */
interface RevokeOthersResult {
	revoked_count: number;
}

/**
 * `sessions revoke` 核心。`<enc_id>` 與 `--all-others` 互斥擇一 —— 都給或都缺都是使用者輸入錯誤，
 * 本地擋下（exit 2），不發任何請求。
 *
 * 撤銷「目前這台裝置」的 session 時，本機 credentials 刻意**不**主動清除：CLI 沒有廉價的方法
 * 分辨「這個 enc_id 是不是自己」（要另外打一次 GET /oauth/sessions 找 is_current 比對，換不到
 * 什麼），讓下一次 personal API 請求自然收到 401、走 personal-client 既有的「Run `wport login`」
 * 提示路徑即可——這與「在另一台裝置上撤銷本機 session」的收尾路徑完全一致，不需要為「自己撤自己」
 * 開特例。
 */
export async function runSessionsRevoke(
	ctx: ResolvedContext,
	encId: string | undefined,
	allOthers: boolean
): Promise<void> {
	const hasEncId = encId !== undefined;
	if (hasEncId === allOthers) {
		throw new CliError('Provide exactly one of <enc_id> or --all-others', ExitCode.InvalidArgument);
	}

	const opts: PersonalRequestOptions = { baseUrl: ctx.baseUrl, locale: ctx.locale, timeoutMs: ctx.timeoutMs };

	if (allOthers) {
		const { body } = await personalDelete(opts, `${OAUTH_SESSIONS_BASE}/others`);
		const result = unwrapDataResponse<RevokeOthersResult>(body);
		if (ctx.format === 'json') {
			printJson(result);
			return;
		}
		process.stdout.write(`Revoked ${result.revoked_count} other session(s).\n`);
		return;
	}

	const trimmed = encId!.trim();
	if (!trimmed) throw new CliError('enc_id must not be empty', ExitCode.InvalidArgument);
	// 端點回 MsgResponse({ success, statusCode, message })，無 `data` 欄位 —— 不走 unwrapDataResponse
	// （那需要 `data` 鍵存在，硬套會誤丟「missing wrapper」）。回應內容本身不影響本地輸出。
	await personalDelete(opts, `${OAUTH_SESSIONS_BASE}/${encodeURIComponent(trimmed)}`);
	if (ctx.format === 'json') {
		printJson({ enc_id: trimmed, revoked: true });
		return;
	}
	process.stdout.write(`Revoked session ${trimmed}.\n`);
}

export function registerSessionsRevoke(parent: Command): void {
	parent
		.command('revoke [enc_id]')
		.description('Revoke a session by enc_id, or every other session with --all-others')
		.option('--all-others', 'revoke every session except the current one')
		.action(async (encId: string | undefined, flags: RevokeFlags, command: Command) => {
			const ctx = resolveContext(command);
			await runSessionsRevoke(ctx, encId, flags.allOthers === true);
		});
}
